import { Routes } from '@angular/router';

import { adminGuard } from './core/guards/admin.guard';
import { authGuard, guestGuard } from './core/guards/auth.guard';
import { permissionGuard } from './core/guards/permission.guard';

export const routes: Routes = [
  {
    path: 'login',
    canMatch: [guestGuard],
    loadComponent: () =>
      import('./features/auth/login/login.component').then((m) => m.LoginComponent),
  },
  {
    // Public customer feedback page — the emailed token is the only credential,
    // so it must sit outside the auth guard.
    path: 'survey/:token',
    loadComponent: () =>
      import('./features/support/survey.component').then((m) => m.SurveyComponent),
  },
  {
    path: '403',
    loadComponent: () =>
      import('./features/errors/forbidden/forbidden.component').then((m) => m.ForbiddenComponent),
  },
  {
    path: '',
    canMatch: [authGuard],
    loadComponent: () => import('./layout/shell/shell.component').then((m) => m.ShellComponent),
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'account',
        loadComponent: () =>
          import('./features/account/account.component').then((m) => m.AccountComponent),
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/dashboard/dashboard.component').then((m) => m.DashboardComponent),
      },
      {
        path: 'calendar',
        canMatch: [permissionGuard('calendar.event.read')],
        loadComponent: () =>
          import('./features/calendar/calendar-page.component').then((m) => m.CalendarPageComponent),
      },
      {
        path: 'admin',
        canMatch: [adminGuard()],
        loadComponent: () =>
          import('./layout/admin-shell/admin-shell.component').then((m) => m.AdminShellComponent),
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/admin/admin-redirect.component').then((m) => m.AdminRedirectComponent),
          },
          {
            path: 'users',
            canMatch: [permissionGuard('admin.users.read')],
            loadComponent: () =>
              import('./features/admin/users-list.component').then((m) => m.AdminUsersListComponent),
          },
          {
            path: 'users/new',
            canMatch: [permissionGuard('admin.users.create')],
            loadComponent: () =>
              import('./features/admin/user-form.component').then((m) => m.AdminUserFormComponent),
          },
          {
            path: 'users/:id',
            canMatch: [permissionGuard('admin.users.read')],
            loadComponent: () =>
              import('./features/admin/user-detail.component').then((m) => m.AdminUserDetailComponent),
          },
          {
            path: 'departments',
            canMatch: [permissionGuard('admin.access.read')],
            loadComponent: () =>
              import('./features/admin/departments-list.component').then((m) => m.AdminDepartmentsListComponent),
          },
          {
            path: 'roles',
            canMatch: [permissionGuard('admin.access.read')],
            loadComponent: () =>
              import('./features/admin/roles-list.component').then((m) => m.AdminRolesListComponent),
          },
          {
            path: 'roles/:id/permissions',
            canMatch: [permissionGuard('admin.access.read')],
            loadComponent: () =>
              import('./features/admin/role-permissions.component').then((m) => m.AdminRolePermissionsComponent),
          },
          {
            path: 'lookups',
            canMatch: [permissionGuard('admin.lookups.manage')],
            loadComponent: () =>
              import('./features/admin/lookups-list.component').then((m) => m.AdminLookupsListComponent),
          },
          {
            path: 'lookups/:id/values',
            canMatch: [permissionGuard('admin.lookups.manage')],
            loadComponent: () =>
              import('./features/admin/lookup-values.component').then((m) => m.AdminLookupValuesComponent),
          },
          {
            path: 'custom-fields',
            canMatch: [permissionGuard('admin.customfields.manage')],
            loadComponent: () =>
              import('./features/admin/custom-fields-list.component').then((m) => m.AdminCustomFieldsListComponent),
          },
          {
            path: 'settings',
            canMatch: [permissionGuard('admin.settings.manage')],
            loadComponent: () =>
              import('./features/admin/settings.component').then((m) => m.AdminSettingsComponent),
          },
          {
            path: 'integrations/google',
            canMatch: [permissionGuard('calendar.integration.manage')],
            loadComponent: () =>
              import('./features/calendar/google-integration.component').then((m) => m.GoogleIntegrationComponent),
          },
          {
            path: 'email',
            canMatch: [permissionGuard('admin.email.manage')],
            loadComponent: () =>
              import('./features/admin/email-config.component').then((m) => m.AdminEmailConfigComponent),
          },
          {
            path: 'email-templates',
            canMatch: [permissionGuard('admin.emailtemplates.manage')],
            loadComponent: () =>
              import('./features/admin/email-templates-list.component').then((m) => m.AdminEmailTemplatesListComponent),
          },
          {
            path: 'email-templates/:id',
            canMatch: [permissionGuard('admin.emailtemplates.manage')],
            loadComponent: () =>
              import('./features/admin/email-template-editor.component').then((m) => m.AdminEmailTemplateEditorComponent),
          },
          {
            path: 'holidays',
            canMatch: [permissionGuard('admin.holidays.manage')],
            loadComponent: () =>
              import('./features/admin/holidays-list.component').then((m) => m.AdminHolidaysListComponent),
          },
          {
            path: 'office-expense-types',
            canMatch: [permissionGuard('admin.expense_type.read')],
            loadComponent: () =>
              import('./features/admin/office-expense-types.component').then((m) => m.AdminOfficeExpenseTypesComponent),
          },
          {
            path: 'subscription-services',
            canMatch: [permissionGuard('admin.subscription_service.read')],
            loadComponent: () =>
              import('./features/admin/subscription-services.component').then((m) => m.AdminSubscriptionServicesComponent),
          },
          {
            path: 'deduction-types',
            canMatch: [permissionGuard('admin.deduction_type.read')],
            loadComponent: () =>
              import('./features/admin/deduction-types.component').then((m) => m.AdminDeductionTypesComponent),
          },
          {
            path: 'approval-levels',
            canMatch: [permissionGuard('admin.settings.manage')],
            loadComponent: () =>
              import('./features/admin/approval-levels.component').then((m) => m.AdminApprovalLevelsComponent),
          },
          {
            path: 'tax-rates',
            canMatch: [permissionGuard('billing.service.read')],
            loadComponent: () =>
              import('./features/admin/tax-rates-list.component').then((m) => m.AdminTaxRatesListComponent),
          },
          {
            path: 'service-catalog',
            canMatch: [permissionGuard('billing.service.read')],
            loadComponent: () =>
              import('./features/billing/service-catalog-list.component').then((m) => m.ServiceCatalogListComponent),
          },
          {
            path: 'chart-of-accounts',
            canMatch: [permissionGuard('finance.coa.read')],
            loadComponent: () =>
              import('./features/finance/chart-of-accounts-list.component').then((m) => m.ChartOfAccountsListComponent),
          },
          {
            path: 'automation',
            canMatch: [permissionGuard('admin.automation.view')],
            loadComponent: () =>
              import('./features/admin/automation-health.component').then((m) => m.AdminAutomationHealthComponent),
          },
          {
            path: 'audit-logs',
            canMatch: [permissionGuard('admin.audit.view')],
            loadComponent: () =>
              import('./features/admin/audit-logs-list.component').then((m) => m.AdminAuditLogsListComponent),
          },
          {
            path: 'crm/score-rules',
            canMatch: [permissionGuard('admin.crm.scoring.manage')],
            loadComponent: () =>
              import('./features/crm/admin/admin-score-rules.component').then((m) => m.AdminScoreRulesComponent),
          },
          {
            path: 'crm/assignment-rules',
            canMatch: [permissionGuard('admin.crm.assignment.manage')],
            loadComponent: () =>
              import('./features/crm/admin/admin-assignment-rules.component').then((m) => m.AdminAssignmentRulesComponent),
          },
          {
            path: 'dashboard-permissions',
            canMatch: [permissionGuard('admin.dashboard.read')],
            loadComponent: () =>
              import('./features/admin/dashboard-permissions.component').then((m) => m.DashboardPermissionsComponent),
          },
        ],
      },
      {
        path: 'crm/leads',
        canMatch: [permissionGuard('crm.lead.read')],
        loadComponent: () =>
          import('./features/crm/leads/leads-list.component').then((m) => m.LeadsListComponent),
      },
      {
        path: 'crm/contacts',
        canMatch: [permissionGuard('crm.contact.read')],
        loadComponent: () =>
          import('./features/crm/contacts/contacts-list.component').then((m) => m.ContactsListComponent),
      },
      {
        path: 'crm/contacts/new',
        canMatch: [permissionGuard('crm.contact.create')],
        loadComponent: () =>
          import('./features/crm/contacts/contact-form.component').then((m) => m.ContactFormComponent),
      },
      {
        path: 'crm/contacts/:id/edit',
        canMatch: [permissionGuard('crm.contact.update')],
        loadComponent: () =>
          import('./features/crm/contacts/contact-form.component').then((m) => m.ContactFormComponent),
      },
      {
        path: 'crm/contacts/:id',
        canMatch: [permissionGuard('crm.contact.read')],
        loadComponent: () =>
          import('./features/crm/contacts/contact-detail.component').then((m) => m.ContactDetailComponent),
      },
      {
        path: 'crm/accounts',
        canMatch: [permissionGuard('crm.account.read')],
        loadComponent: () =>
          import('./features/crm/accounts/accounts-list.component').then((m) => m.AccountsListComponent),
      },
      {
        path: 'crm/accounts/new',
        canMatch: [permissionGuard('crm.account.create')],
        loadComponent: () =>
          import('./features/crm/accounts/account-form.component').then((m) => m.AccountFormComponent),
      },
      {
        path: 'crm/accounts/:id/edit',
        canMatch: [permissionGuard('crm.account.update')],
        loadComponent: () =>
          import('./features/crm/accounts/account-form.component').then((m) => m.AccountFormComponent),
      },
      {
        path: 'crm/accounts/:id',
        canMatch: [permissionGuard('crm.account.read')],
        loadComponent: () =>
          import('./features/crm/accounts/account-detail.component').then((m) => m.AccountDetailComponent),
      },
      {
        path: 'crm/pipeline',
        canMatch: [permissionGuard('crm.opportunity.read')],
        loadComponent: () =>
          import('./features/crm/opportunities/pipeline-board.component').then((m) => m.PipelineBoardComponent),
      },
      {
        path: 'crm/tasks',
        canMatch: [permissionGuard('crm.activity.read')],
        loadComponent: () =>
          import('./features/crm/tasks/tasks-list.component').then((m) => m.TasksListComponent),
      },
      {
        path: 'crm/leads/unassigned',
        canMatch: [permissionGuard('crm.lead.read')],
        loadComponent: () =>
          import('./features/crm/leads/assignment-queue.component').then(
            (m) => m.AssignmentQueueComponent,
          ),
      },
      {
        path: 'crm/leads/new',
        canMatch: [permissionGuard('crm.lead.create')],
        loadComponent: () =>
          import('./features/crm/leads/lead-form.component').then((m) => m.LeadFormComponent),
      },
      {
        path: 'crm/leads/:id/edit',
        canMatch: [permissionGuard('crm.lead.update')],
        loadComponent: () =>
          import('./features/crm/leads/lead-form.component').then((m) => m.LeadFormComponent),
      },
      {
        path: 'crm/leads/:id',
        canMatch: [permissionGuard('crm.lead.read')],
        loadComponent: () =>
          import('./features/crm/leads/lead-detail.component').then((m) => m.LeadDetailComponent),
      },
      {
        path: 'agreements',
        canMatch: [permissionGuard('agreements.agreement.read')],
        loadComponent: () =>
          import('./features/agreements/agreements-list.component').then((m) => m.AgreementsListComponent),
      },
      {
        path: 'agreements/clients',
        canMatch: [permissionGuard('agreements.agreement.read')],
        loadComponent: () =>
          import('./features/agreements/clients-list.component').then((m) => m.ClientsListComponent),
      },
      {
        path: 'agreements/onboard',
        canMatch: [permissionGuard('agreements.agreement.create')],
        loadComponent: () =>
          import('./features/agreements/agreement-onboard.component').then((m) => m.AgreementOnboardComponent),
      },
      {
        path: 'agreements/:id/renew',
        canMatch: [permissionGuard('agreements.agreement.renew')],
        loadComponent: () =>
          import('./features/agreements/agreement-renew.component').then((m) => m.AgreementRenewComponent),
      },
      {
        path: 'agreements/:id/edit',
        canMatch: [permissionGuard('agreements.agreement.update')],
        loadComponent: () =>
          import('./features/agreements/agreement-edit.component').then((m) => m.AgreementEditComponent),
      },
      {
        path: 'agreements/:id',
        canMatch: [permissionGuard('agreements.agreement.read')],
        loadComponent: () =>
          import('./features/agreements/agreement-detail.component').then((m) => m.AgreementDetailComponent),
      },
      {
        path: 'billing/service-catalog',
        canMatch: [permissionGuard('billing.service.read')],
        loadComponent: () =>
          import('./features/billing/service-catalog-list.component').then((m) => m.ServiceCatalogListComponent),
      },
      {
        path: 'billing/service-catalog/new',
        canMatch: [permissionGuard('billing.service.create')],
        loadComponent: () =>
          import('./features/billing/service-catalog-form.component').then((m) => m.ServiceCatalogFormComponent),
      },
      {
        path: 'billing/service-catalog/:id/edit',
        canMatch: [permissionGuard('billing.service.update')],
        loadComponent: () =>
          import('./features/billing/service-catalog-form.component').then((m) => m.ServiceCatalogFormComponent),
      },
      {
        path: 'billing/subscriptions',
        canMatch: [permissionGuard('billing.subscription.read')],
        loadComponent: () =>
          import('./features/billing/subscriptions-list.component').then((m) => m.SubscriptionsListComponent),
      },
      {
        path: 'billing/subscriptions/new',
        canMatch: [permissionGuard('billing.subscription.create')],
        loadComponent: () =>
          import('./features/billing/subscription-form.component').then((m) => m.SubscriptionFormComponent),
      },
      {
        path: 'billing/subscriptions/:id/edit',
        canMatch: [permissionGuard('billing.subscription.update')],
        loadComponent: () =>
          import('./features/billing/subscription-form.component').then((m) => m.SubscriptionFormComponent),
      },
      {
        path: 'billing/invoices',
        canMatch: [permissionGuard('billing.invoice.read')],
        loadComponent: () =>
          import('./features/billing/invoices-list.component').then((m) => m.InvoicesListComponent),
      },
      {
        path: 'billing/invoices/new',
        canMatch: [permissionGuard('billing.invoice.create')],
        loadComponent: () =>
          import('./features/billing/invoice-form.component').then((m) => m.InvoiceFormComponent),
      },
      {
        path: 'billing/invoices/:id/edit',
        canMatch: [permissionGuard('billing.invoice.update')],
        loadComponent: () =>
          import('./features/billing/invoice-form.component').then((m) => m.InvoiceFormComponent),
      },
      {
        path: 'billing/invoices/:id',
        canMatch: [permissionGuard('billing.invoice.read')],
        loadComponent: () =>
          import('./features/billing/invoice-detail.component').then((m) => m.InvoiceDetailComponent),
      },
      {
        path: 'billing/quotations',
        canMatch: [permissionGuard('billing.quotation.read')],
        loadComponent: () =>
          import('./features/billing/quotations/quotations-list.component').then((m) => m.QuotationsListComponent),
      },
      {
        path: 'billing/quotations/new',
        canMatch: [permissionGuard('billing.quotation.create')],
        loadComponent: () =>
          import('./features/billing/quotations/quotation-form.component').then((m) => m.QuotationFormComponent),
      },
      {
        path: 'billing/quotations/:id/edit',
        canMatch: [permissionGuard('billing.quotation.update')],
        loadComponent: () =>
          import('./features/billing/quotations/quotation-form.component').then((m) => m.QuotationFormComponent),
      },
      {
        path: 'billing/quotations/:id',
        canMatch: [permissionGuard('billing.quotation.read')],
        loadComponent: () =>
          import('./features/billing/quotations/quotation-detail.component').then((m) => m.QuotationDetailComponent),
      },
      {
        path: 'marketing/campaigns',
        canMatch: [permissionGuard('marketing.campaign.read')],
        loadComponent: () =>
          import('./features/marketing/campaigns-list.component').then((m) => m.CampaignsListComponent),
      },
      {
        path: 'marketing/campaigns/new',
        canMatch: [permissionGuard('marketing.campaign.create')],
        loadComponent: () =>
          import('./features/marketing/campaign-form.component').then((m) => m.CampaignFormComponent),
      },
      {
        path: 'marketing/campaigns/:id/edit',
        canMatch: [permissionGuard('marketing.campaign.update')],
        loadComponent: () =>
          import('./features/marketing/campaign-form.component').then((m) => m.CampaignFormComponent),
      },
      {
        path: 'reporting/dashboard',
        canMatch: [permissionGuard('reporting.dashboard.view')],
        loadComponent: () =>
          import('./features/reporting/reporting-dashboard.component').then((m) => m.ReportingDashboardComponent),
      },
      {
        path: 'reporting/builder',
        canMatch: [permissionGuard('reporting.report.create')],
        loadComponent: () =>
          import('./features/reporting/report-builder.component').then((m) => m.ReportBuilderComponent),
      },
      {
        path: 'reporting/reports',
        canMatch: [permissionGuard('reporting.report.read')],
        loadComponent: () =>
          import('./features/reporting/reports-hub.component').then((m) => m.ReportsHubComponent),
      },
      {
        path: 'support/tickets',
        canMatch: [permissionGuard('support.ticket.read')],
        loadComponent: () =>
          import('./features/support/tickets-list.component').then((m) => m.TicketsListComponent),
      },
      {
        path: 'support/tickets/new',
        canMatch: [permissionGuard('support.ticket.create')],
        loadComponent: () =>
          import('./features/support/ticket-form.component').then((m) => m.TicketFormComponent),
      },
      {
        path: 'support/tickets/:id',
        canMatch: [permissionGuard('support.ticket.read')],
        loadComponent: () =>
          import('./features/support/ticket-detail.component').then((m) => m.TicketDetailComponent),
      },
      {
        path: 'support/knowledge',
        canMatch: [permissionGuard('support.knowledge_article.read')],
        loadComponent: () =>
          import('./features/support/kb-list.component').then((m) => m.KbListComponent),
      },
      {
        path: 'finance/dashboard',
        canMatch: [permissionGuard('finance.report.read')],
        loadComponent: () =>
          import('./features/finance/finance-dashboard.component').then((m) => m.FinanceDashboardComponent),
      },
      {
        path: 'finance/reports',
        canMatch: [permissionGuard('finance.report.read')],
        loadComponent: () =>
          import('./features/finance/finance-reports.component').then((m) => m.FinanceReportsComponent),
      },
      {
        path: 'finance/chart-of-accounts',
        canMatch: [permissionGuard('finance.coa.read')],
        loadComponent: () =>
          import('./features/finance/chart-of-accounts-list.component').then((m) => m.ChartOfAccountsListComponent),
      },
      {
        path: 'finance/chart-of-accounts/new',
        canMatch: [permissionGuard('finance.coa.create')],
        loadComponent: () =>
          import('./features/finance/chart-of-accounts-form.component').then((m) => m.ChartOfAccountsFormComponent),
      },
      {
        path: 'finance/chart-of-accounts/:id/edit',
        canMatch: [permissionGuard('finance.coa.update')],
        loadComponent: () =>
          import('./features/finance/chart-of-accounts-form.component').then((m) => m.ChartOfAccountsFormComponent),
      },
      {
        path: 'finance/ledger',
        canMatch: [permissionGuard('finance.ledger.view')],
        loadComponent: () =>
          import('./features/finance/ledger-viewer.component').then((m) => m.LedgerViewerComponent),
      },
      {
        path: 'finance/projects',
        canMatch: [permissionGuard('finance.project.read')],
        loadComponent: () =>
          import('./features/finance/projects-list.component').then((m) => m.ProjectsListComponent),
      },
      {
        path: 'finance/projects/new',
        canMatch: [permissionGuard('finance.project.create')],
        loadComponent: () =>
          import('./features/finance/projects-form.component').then((m) => m.ProjectsFormComponent),
      },
      {
        path: 'finance/projects/:id/edit',
        canMatch: [permissionGuard('finance.project.update')],
        loadComponent: () =>
          import('./features/finance/projects-form.component').then((m) => m.ProjectsFormComponent),
      },
      {
        path: 'finance/commissions',
        canMatch: [permissionGuard('finance.commission.view')],
        loadComponent: () =>
          import('./features/finance/commissions-list.component').then((m) => m.CommissionsListComponent),
      },
      {
        path: 'finance/expenses',
        canMatch: [permissionGuard('finance.expense.read')],
        loadComponent: () =>
          import('./features/finance/expenses-list.component').then((m) => m.ExpensesListComponent),
      },
      {
        path: 'finance/expenses/new',
        canMatch: [permissionGuard('finance.expense.create')],
        loadComponent: () =>
          import('./features/finance/expense-form.component').then((m) => m.ExpenseFormComponent),
      },
      {
        path: 'finance/expenses/:id/edit',
        canMatch: [permissionGuard('finance.expense.update')],
        loadComponent: () =>
          import('./features/finance/expense-form.component').then((m) => m.ExpenseFormComponent),
      },
      {
        path: 'finance/contractors',
        canMatch: [permissionGuard('finance.contractor.read')],
        loadComponent: () =>
          import('./features/finance/contractors-list.component').then((m) => m.ContractorsListComponent),
      },
      {
        path: 'finance/contractors/new',
        canMatch: [permissionGuard('finance.contractor.create')],
        loadComponent: () =>
          import('./features/finance/contractor-form.component').then((m) => m.ContractorFormComponent),
      },
      {
        path: 'finance/contractors/:id/edit',
        canMatch: [permissionGuard('finance.contractor.update')],
        loadComponent: () =>
          import('./features/finance/contractor-form.component').then((m) => m.ContractorFormComponent),
      },
      {
        path: 'finance/contractor-payments',
        canMatch: [permissionGuard('finance.contractor.payment.read')],
        loadComponent: () =>
          import('./features/finance/contractor-payments-list.component').then((m) => m.ContractorPaymentsListComponent),
      },
      {
        path: 'finance/company-expenses',
        canMatch: [permissionGuard('finance.company_expense.read')],
        loadComponent: () =>
          import('./features/finance/company-expenses-list.component').then((m) => m.CompanyExpensesListComponent),
      },
      {
        path: 'finance/company-expenses/new',
        canMatch: [permissionGuard('finance.company_expense.create')],
        loadComponent: () =>
          import('./features/finance/company-expense-form.component').then((m) => m.CompanyExpenseFormComponent),
      },
      {
        path: 'finance/company-expenses/:id/edit',
        canMatch: [permissionGuard('finance.company_expense.update')],
        loadComponent: () =>
          import('./features/finance/company-expense-form.component').then((m) => m.CompanyExpenseFormComponent),
      },
      {
        path: 'finance/reimbursements',
        canMatch: [permissionGuard('finance.reimbursement.read')],
        loadComponent: () =>
          import('./features/finance/reimbursements/reimbursements-list.component').then((m) => m.ReimbursementsListComponent),
      },
      {
        path: 'finance/reimbursements/new',
        canMatch: [permissionGuard('finance.reimbursement.create')],
        loadComponent: () =>
          import('./features/finance/reimbursements/reimbursement-form.component').then((m) => m.ReimbursementFormComponent),
      },
      {
        path: 'finance/reimbursements/:id/edit',
        canMatch: [permissionGuard('finance.reimbursement.update')],
        loadComponent: () =>
          import('./features/finance/reimbursements/reimbursement-form.component').then((m) => m.ReimbursementFormComponent),
      },
      {
        path: 'finance/employees',
        canMatch: [permissionGuard('finance.employee.read')],
        loadComponent: () =>
          import('./features/finance/employees-list.component').then((m) => m.EmployeesListComponent),
      },
      {
        path: 'finance/employees/new',
        canMatch: [permissionGuard('finance.employee.create')],
        loadComponent: () =>
          import('./features/finance/employee-form.component').then((m) => m.EmployeeFormComponent),
      },
      {
        path: 'finance/employees/:id/edit',
        canMatch: [permissionGuard('finance.employee.update')],
        loadComponent: () =>
          import('./features/finance/employee-form.component').then((m) => m.EmployeeFormComponent),
      },
      {
        path: 'finance/salary-records',
        canMatch: [permissionGuard('finance.salary.read')],
        loadComponent: () =>
          import('./features/finance/salary-records-list.component').then((m) => m.SalaryRecordsListComponent),
      },
      {
        path: 'finance/salary-records/new',
        canMatch: [permissionGuard('finance.salary.create')],
        loadComponent: () =>
          import('./features/finance/salary-record-form.component').then((m) => m.SalaryRecordFormComponent),
      },
      {
        path: 'finance/salary-records/:id/edit',
        canMatch: [permissionGuard('finance.salary.update')],
        loadComponent: () =>
          import('./features/finance/salary-record-form.component').then((m) => m.SalaryRecordFormComponent),
      },
      {
        path: 'finance/loans',
        canMatch: [permissionGuard('finance.loan.read')],
        loadComponent: () =>
          import('./features/finance/loans-list.component').then((m) => m.LoansListComponent),
      },
      {
        path: 'finance/loans/new',
        canMatch: [permissionGuard('finance.loan.create')],
        loadComponent: () =>
          import('./features/finance/loan-form.component').then((m) => m.LoanFormComponent),
      },
      {
        path: 'finance/loans/:id',
        canMatch: [permissionGuard('finance.loan.read')],
        loadComponent: () =>
          import('./features/finance/loan-detail.component').then((m) => m.LoanDetailComponent),
      },
      {
        path: 'finance/loans/:id/edit',
        canMatch: [permissionGuard('finance.loan.update')],
        loadComponent: () =>
          import('./features/finance/loan-form.component').then((m) => m.LoanFormComponent),
      },
    ],
  },
  { path: '**', redirectTo: 'dashboard' },
];
